import { useEffect, useMemo, useRef, useState } from 'react';

import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '@src/firebase/firebase';
import { FirestoreListSyncUtil } from '@src/util/firestoreListSync';

const firestoreListSyncUtil = new FirestoreListSyncUtil();

interface Identifiable {
  id: string;
}

type LiveOverride<T> = { kind: 'updated'; item: T } | { kind: 'deleted' };

interface ActiveSubscription {
  path: string;
  unsubscribe: () => void;
}

export interface UseFirestoreListSyncOptions<T extends Identifiable> {
  /** Current page items, e.g. the hits returned by an Elasticsearch query. */
  items: T[];
  /**
   * Resolves the Firestore document path for an item, or null to skip it.
   * Memoize with useCallback; changing its identity re-evaluates subscriptions.
   */
  getDocPath: (item: T) => string | null;
  /** Set to false to pause all listeners (e.g. while a tab is hidden). */
  enabled?: boolean;
  /**
   * Items just created locally that don't appear in `items` yet because the search index
   * hasn't caught up. They're subscribed to and rendered like any other row until a refetch
   * includes them in `items` (at which point this entry is ignored by id). Use this to make a
   * brand-new document visible to its creator immediately, without waiting for Elasticsearch.
   */
  pendingItems?: T[];
  /**
   * Maps a Firestore snapshot into the list item shape. Defaults to merging the
   * document data over the current item with Timestamps serialized to the
   * `{ _seconds, _nanoseconds }` shape Elasticsearch lists already render.
   */
  transform?: (docData: Record<string, unknown>, currentItem: T) => T;
}

export interface UseFirestoreListSyncResult<T> {
  /** The input items with realtime updates applied and deleted docs removed. */
  items: T[];
  /** Ids of visible items whose Firestore document no longer exists. */
  deletedIds: string[];
  /**
   * True while at least one visible item is still waiting for its first
   * Firestore snapshot. Combine this with the server-fetch loading flag so the
   * list isn't painted before the realtime layer has reconciled it (otherwise a
   * row can flash in from the server result and then update/disappear).
   */
  isSyncing: boolean;
}

const defaultTransform = <T extends Identifiable>(
  docData: Record<string, unknown>,
  currentItem: T,
): T => ({
  ...currentItem,
  ...firestoreListSyncUtil.serializeTimestamps(docData),
  id: currentItem.id,
});

/**
 * "What you see is what you listen to": keeps a server-fetched page (typically an
 * Elasticsearch result, which is eventually consistent) synchronized in realtime by
 * subscribing to the Firestore document of each visible item — and nothing else.
 *
 * - Listener count is bounded by the page size, so it scales regardless of the
 *   collection size.
 * - The first snapshot of every listener delivers the authoritative Firestore data,
 *   which also heals stale Elasticsearch hits immediately after navigation.
 * - Updates and deletions reflect in realtime; brand-new documents appear once the
 *   search index catches up and the list is refetched — or immediately for their creator
 *   via `pendingItems` (see the option docs below).
 */
export const useFirestoreListSync = <T extends Identifiable>({
  items,
  getDocPath,
  enabled = true,
  transform,
  pendingItems,
}: UseFirestoreListSyncOptions<T>): UseFirestoreListSyncResult<T> => {
  const [overrides, setOverrides] = useState<Map<string, LiveOverride<T>>>(new Map());
  // Ids whose listener has already delivered its first snapshot. Until every
  // visible item is in this set the page is still being reconciled.
  const [hydratedIds, setHydratedIds] = useState<Set<string>>(new Set());

  const subscriptionsRef = useRef<Map<string, ActiveSubscription>>(new Map());

  // Pending creates are prepended to the server page so the just-created row shows on top.
  // Once Elasticsearch catches up and `items` includes the id, the server item wins and the
  // pending entry is dropped — so the row never duplicates.
  const effectiveItems = useMemo(() => {
    if (!pendingItems?.length) return items;
    const presentIds = new Set(items.map(item => item.id));
    const extras = pendingItems.filter(item => item?.id && !presentIds.has(item.id));
    return extras.length ? [...extras, ...items] : items;
  }, [items, pendingItems]);

  // Refs keep the latest callbacks without forcing listeners to resubscribe
  // when callers pass inline functions.
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const itemsRef = useRef(effectiveItems);
  itemsRef.current = effectiveItems;

  const markHydrated = (id: string) =>
    setHydratedIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  useEffect(() => {
    const subscriptions = subscriptionsRef.current;

    if (!enabled) {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      subscriptions.clear();
      setHydratedIds(prev => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const wantedPaths = new Map<string, string>();
    effectiveItems.forEach(item => {
      if (!item?.id) return;
      const path = getDocPath(item);
      if (path) wantedPaths.set(item.id, path);
    });

    // Drop listeners for items that left the page (or whose path changed).
    subscriptions.forEach((subscription, id) => {
      if (wantedPaths.get(id) !== subscription.path) {
        subscription.unsubscribe();
        subscriptions.delete(id);
      }
    });

    // Subscribe to items that entered the page.
    wantedPaths.forEach((path, id) => {
      if (subscriptions.has(id)) return;

      const unsubscribe = onSnapshot(
        doc(db, path),
        snapshot => {
          markHydrated(id);
          setOverrides(prev => {
            const next = new Map(prev);
            if (!snapshot.exists()) {
              next.set(id, { kind: 'deleted' });
            } else {
              const currentItem =
                itemsRef.current.find(item => item.id === id) ?? ({ id } as T);
              const applyTransform = transformRef.current ?? defaultTransform;
              next.set(id, {
                kind: 'updated',
                item: applyTransform(snapshot.data() as Record<string, unknown>, currentItem),
              });
            }
            return next;
          });
        },
        error => {
          // Keep showing the server item if the listener fails (e.g. permissions),
          // but still release the syncing gate so the list isn't stuck loading.
          markHydrated(id);
          console.error(`useFirestoreListSync: listener failed for ${path}`, error);
        },
      );

      subscriptions.set(id, { path, unsubscribe });
    });

    // Prune overrides for items no longer visible so state stays page-sized.
    setOverrides(prev => {
      if (Array.from(prev.keys()).every(id => wantedPaths.has(id))) return prev;
      const next = new Map<string, LiveOverride<T>>();
      prev.forEach((override, id) => {
        if (wantedPaths.has(id)) next.set(id, override);
      });
      return next;
    });

    // Same for the hydration set, so leftover ids from a previous page don't
    // keep `isSyncing` from reflecting the current page.
    setHydratedIds(prev => {
      if (Array.from(prev).every(id => wantedPaths.has(id))) return prev;
      const next = new Set<string>();
      prev.forEach(id => {
        if (wantedPaths.has(id)) next.add(id);
      });
      return next;
    });
  }, [effectiveItems, getDocPath, enabled]);

  // Release every listener on unmount.
  useEffect(
    () => () => {
      subscriptionsRef.current.forEach(subscription => subscription.unsubscribe());
      subscriptionsRef.current.clear();
    },
    [],
  );

  return useMemo(() => {
    const liveItems: T[] = [];
    const deletedIds: string[] = [];
    let isSyncing = false;

    effectiveItems.forEach(item => {
      // A visible item with a doc path that hasn't been hydrated yet means the
      // realtime layer is still catching up with the server result.
      if (enabled && item?.id && getDocPath(item) && !hydratedIds.has(item.id)) {
        isSyncing = true;
      }

      const override = overrides.get(item.id);
      if (!override) {
        liveItems.push(item);
        return;
      }
      if (override.kind === 'deleted') {
        deletedIds.push(item.id);
        return;
      }
      liveItems.push(override.item);
    });

    return { items: liveItems, deletedIds, isSyncing };
  }, [effectiveItems, overrides, hydratedIds, enabled, getDocPath]);
};
